/** Expand only opted-in collection cards, preserving their configured blocks and styles. */
class ColorVariantCards extends HTMLElement {
  connectedCallback() {
    // Also runs for cards inserted by filtering, sorting and infinite scroll.
    queueMicrotask(() => this.#expand());
  }

  #expand() {
    if (!this.isConnected) return;
    const original = this.closest('.product-grid__item');
    if (!original) return;
    const variants = this.querySelectorAll('template[data-color-variant]');
    if (!variants.length) return;

    const cards = document.createDocumentFragment();
    for (const variant of variants) {
      const card = original.cloneNode(true);
      card.querySelectorAll('color-variant-cards').forEach((element) => element.remove());
      const { variantId, available, mediaUrl } = variant.dataset;
      card.dataset.colorVariantId = variantId;
      this.#uniqueIds(card, variantId);

      // Keep every way of opening the card tied to this color, including quick add.
      for (const link of card.querySelectorAll('a[href*="/products/"]')) {
        const url = new URL(link.href, window.location.origin);
        url.searchParams.set('variant', variantId);
        link.href = url.href;
      }
      const productCard = card.querySelector('product-card');
      if (productCard && mediaUrl) productCard.dataset.featuredMediaUrl = mediaUrl;

      const price = variant.content.querySelector('template[data-price]');
      for (const container of card.querySelectorAll('product-price [ref="priceContainer"]')) {
        if (!price) continue;
        const replacement = price.content.cloneNode(true);
        const previousSale = container.querySelector('.price');
        const previousCompare = container.querySelector('.compare-at-price');
        // Honor the configured price order as well as its typography and wrapper styles.
        if (previousSale && previousCompare && (previousCompare.compareDocumentPosition(previousSale) & Node.DOCUMENT_POSITION_FOLLOWING)) {
          const nextCompare = replacement.querySelector('.compare-at-price');
          const nextSale = replacement.querySelector('.price');
          if (nextCompare && nextSale) nextSale.before(nextCompare);
        }
        container.replaceWith(replacement);
      }
      const gallery = card.querySelector('[ref="cardGallery"]');
      if (gallery) {
        gallery.querySelectorAll('.product-badges').forEach((badge) => badge.remove());
        const badge = variant.content.querySelector('template[data-badge]');
        if (badge) gallery.append(badge.content.cloneNode(true));

        const media = variant.content.querySelector('template[data-media]');
        const galleryLink = gallery.querySelector('[ref="cardGalleryLink"]');
        if (media && galleryLink) {
          const existing = galleryLink.querySelector('.product-media-container');
          const container = document.createElement('div');
          container.className = existing?.className || 'product-media-container media-fit product-media-container--image';
          if (existing?.getAttribute('style')) container.setAttribute('style', existing.getAttribute('style'));
          container.append(media.content.cloneNode(true));
          galleryLink.replaceChildren(container);
        }
      }

      for (const quickAdd of card.querySelectorAll('quick-add-component')) {
        for (const input of quickAdd.querySelectorAll('input[name="id"]')) {
          input.value = variantId;
          input.disabled = available !== 'true';
        }
        // One-option color cards already identify an exact variant. Multi-option cards still choose a size.
        if (quickAdd.dataset.productOptionsCount === '1' && quickAdd.dataset.usesSellingPlans !== 'true') {
          // If the original card had no add form, keep its Choose button and use the variant URL.
          if (quickAdd.querySelector('input[name="id"]')) {
            quickAdd.dataset.quickAddButton = available === 'true' ? 'add' : 'choose';
          }
        }
      }
      // Preserve product IDs and page markers: pagination still follows Shopify's product pages.
      cards.append(card);
    }
    original.replaceWith(cards);
  }

  #uniqueIds(card, variantId) {
    const elements = [card, ...card.querySelectorAll('[id]')];
    const ids = new Map();
    for (const element of elements) {
      if (!element.id) continue;
      const previous = element.id;
      element.id = `${previous}-color-${variantId}`;
      ids.set(previous, element.id);
    }
    for (const element of card.querySelectorAll('*')) {
      for (const attribute of [...element.attributes]) {
        if (['for', 'form', 'aria-labelledby', 'aria-describedby', 'aria-controls'].includes(attribute.name)) {
          element.setAttribute(attribute.name, attribute.value.split(' ').map((id) => ids.get(id) || id).join(' '));
        } else if (attribute.name === 'href' || attribute.name.startsWith('on:')) {
          element.setAttribute(attribute.name, attribute.value.replace(/#([\w-]+)/g, (match, id) => ids.has(id) ? `#${ids.get(id)}` : match));
        }
      }
    }
  }
}

if (!customElements.get('color-variant-cards')) {
  customElements.define('color-variant-cards', ColorVariantCards);
}
